import { useState } from 'react';
import { Button, OtpInput } from '@/components/ui';
import { useOtpStore, useOtpSubmitting, useOtpError } from '@/store/otpStore';

const TEST_ID_PREFIX = 'otp-form';

const OTP_LENGTH = 6;

export interface OtpFormProps {
  /** Visual style — defaults to "modal" for use inside Dialog. */
  variant?: 'modal' | 'inline';
}

/**
 * Shared OTP entry form used by both `<OtpModal>` and `<OtpSlot>`. Reads
 * state from `useOtpStore`. Submitting calls `submitOtp` which resolves
 * the in-flight requestOtp promise; dismissing calls `cancelOtp`.
 */
export function OtpForm({ variant = 'modal' }: OtpFormProps) {
  const submitting = useOtpSubmitting();
  const error = useOtpError();
  const submitOtp = useOtpStore((s) => s.submitOtp);
  const cancelOtp = useOtpStore((s) => s.cancelOtp);

  const [code, setCode] = useState('');

  function handleSubmit(value: string) {
    if (value.length !== OTP_LENGTH || submitting) return;
    submitOtp(value);
  }

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-lg font-semibold text-primary">Verification</h2>

      <p className="text-center text-base text-primary">
        A text message with a verification code has been sent to your phone number. Check your phone &amp; enter your
        verification code.
      </p>

      <div className="flex flex-col items-center gap-2">
        <p className="text-center font-light text-primary">Please enter the code here</p>
        <OtpInput
          value={code}
          onChange={(next) => {
            setCode(next);
            if (error === 'invalid') {
              // user is typing a new code — clear stale error
            }
          }}
          onComplete={handleSubmit}
          length={OTP_LENGTH}
          disabled={submitting}
          autoFocus
        />
      </div>

      {error === 'invalid' && (
        <p data-testid={`${TEST_ID_PREFIX}-error`} className="text-center text-sm text-danger-alt" role="alert">
          Invalid code. Please try again.
        </p>
      )}

      {/* TODO: wire "Resend code" once the api exposes a resend endpoint. The
          OTP is sent by the backend as part of the request that raised the
          challenge, and the modal has no handle to re-trigger it, so the link
          is shown disabled rather than as a dead action. */}
      <p className="flex items-center justify-center gap-2 text-center text-base text-primary">
        Did not get a code?
        <span className="font-semibold text-muted">Resend code</span>
      </p>

      <div className="flex items-center justify-between gap-2">
        {variant === 'modal' && (
          <Button
            data-testid={`${TEST_ID_PREFIX}-cancel-button`}
            variant="outline"
            onClick={cancelOtp}
            disabled={submitting}
            type="button"
          >
            Cancel
          </Button>
        )}
        <Button
          data-testid={`${TEST_ID_PREFIX}-submit-button`}
          onClick={() => handleSubmit(code)}
          disabled={code.length !== OTP_LENGTH || submitting}
          type="button"
        >
          {submitting ? 'Verifying…' : 'Verify'}
        </Button>
      </div>
    </div>
  );
}
